import { useEffect, useState } from 'react';
import axios from 'axios';
import { Link } from 'react-router-dom';
import LayoutTemplatePreview from '../components/LayoutTemplatePreview';
import { createContentLayout, deleteContentLayout, getContentLayouts, updateContentLayout } from '../features/edit-page/api/layoutApi';
import BackButton from '../components/BackButton';

export default function LayoutTemplatesSettings() {
  const [templates, setTemplates] = useState([]);
  const [presets, setPresets] = useState([]);
  const [books, setBooks] = useState([]);
  const [source, setSource] = useState('all');
  const [search, setSearch] = useState('');
  const [format, setFormat] = useState('all');
  const [selectedId, setSelectedId] = useState('');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [saving, setSaving] = useState(false);
  const [width, setWidth] = useState(1838);
  const [height, setHeight] = useState(1876);
  const selected = templates.find((item) => item._id === selectedId);
  useEffect(() => {
    let live = true;
    Promise.all([getContentLayouts(), axios.get('/api/design-presets'), axios.get('/api/list_books')])
      .then(([layouts, styles, allBooks]) => {
        if (live) {
          const items = layouts.data.content_layouts || [];
          setTemplates(items); setPresets(styles.data); setBooks(allBooks.data);
          setSelectedId((current) => current || items[0]?._id || '');
        }
      }).catch(() => { if (live) setError('Could not load the layout catalog. Reload to try again.'); })
      .finally(() => { if (live) setLoading(false); });
    return () => { live = false; };
  }, []);
  const refresh = async () => setTemplates((await getContentLayouts()).data.content_layouts || []);
  const mutate = async (action) => {
    setSaving(true); setError('');
    try { await action(); await refresh(); }
    catch (failure) { setError(failure.response?.data?.error || 'Could not save the template.'); }
    finally { setSaving(false); }
  };
  const formatKey = (item) => item.format ? `${item.format.width}x${item.format.height}` : 'unresolved';
  const formats = [...new Set(templates.filter((item) => item.format).map(formatKey))].sort((a, b) => {
    const [aw, ah] = a.split('x').map(Number);
    const [bw, bh] = b.split('x').map(Number);
    return aw - bw || ah - bh;
  });
  const filtered = templates.filter((item) =>
    (source === 'all' || (source === 'global' ? !item.book_id : item.book_id === source)) &&
    (format === 'all' || formatKey(item) === format) &&
    `${item.name || ''} ${item.description || ''}`.toLocaleLowerCase().includes(search.trim().toLocaleLowerCase()));
  const hasFilters = source !== 'all' || format !== 'all' || Boolean(search);
  return <main className="mx-auto max-w-7xl space-y-5 p-6">
    <BackButton />
    <header className="flex flex-wrap items-center justify-between gap-3">
      <div><h1 className="text-2xl font-bold">Layout templates</h1><p className="mt-1 text-sm text-slate-600">Open a layout to see an example. Copy it from a page’s Page layout tab.</p></div>
      <Link className="rounded-lg border bg-white px-4 py-2 text-sm" to="/text-styles">Manage text styles</Link>
    </header>
    {error && <p role="alert" className="rounded border border-red-200 bg-red-50 p-3 text-sm text-red-800">{error}</p>}
    <div className="grid items-start gap-5 lg:grid-cols-[260px_minmax(0,1fr)]">
      <aside aria-label="Browse layout templates" className="flex min-h-0 flex-col rounded-xl border border-slate-200 bg-white lg:sticky lg:top-20 lg:max-h-[calc(100dvh-6rem)]">
        <div className="shrink-0 space-y-3 border-b border-slate-200 p-3">
          <label className="block text-xs font-semibold text-slate-700">Search templates
            <input type="search" value={search} onChange={(event) => setSearch(event.target.value)} placeholder="Name or description…" className="mt-1 w-full rounded-lg border border-slate-300 px-3 py-2 text-sm font-normal" />
          </label>
          <label className="block text-xs font-semibold text-slate-700">Page size
            <select value={format} onChange={(event) => setFormat(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm font-normal">
              <option value="all">All page sizes</option>
              {formats.map((value) => <option key={value} value={value}>{value.replace('x', ' × ')} px</option>)}
              {templates.some((item) => !item.format) && <option value="unresolved">Size unresolved</option>}
            </select>
          </label>
          <label className="block text-xs font-semibold text-slate-700">Source
            <select value={source} onChange={(event) => setSource(event.target.value)} className="mt-1 w-full rounded-lg border border-slate-300 p-2 text-sm font-normal">
              <option value="all">All accessible templates</option><option value="global">Shared catalog</option>
              {books.map((book) => <option key={book._id} value={book._id}>{book.name}</option>)}
            </select>
          </label>
          <div className="flex items-center justify-between gap-2 text-xs text-slate-500" aria-live="polite">
            <span>{filtered.length} of {templates.length} templates</span>
            {hasFilters && <button type="button" onClick={() => { setSearch(''); setFormat('all'); setSource('all'); }} className="font-semibold text-blue-700 hover:underline">Clear filters</button>}
          </div>
        </div>
        <div className="min-h-0 max-h-80 space-y-1.5 overflow-y-auto overscroll-contain p-2 lg:max-h-none" aria-label="Layout template results">
          {loading && <p className="p-2 text-sm text-slate-600">Loading templates…</p>}
          {!loading && !filtered.length && <p className="p-2 text-sm text-slate-600">{templates.length ? 'No templates match these filters.' : 'No templates yet. Save a page layout as a template from the page editor.'}</p>}
          {filtered.map((template) => {
            const count = (template.text_items || []).filter((item) => item.visibleOnCanvas !== false).length;
            return <button type="button" key={template._id} aria-pressed={selectedId === template._id} onClick={() => { setSelectedId(template._id); setWidth(template.format?.width || 1838); setHeight(template.format?.height || 1876); }}
              className={`block w-full rounded-lg border px-3 py-2.5 text-left ${selectedId === template._id ? 'border-violet-400 bg-violet-50' : 'border-transparent bg-slate-50 hover:border-slate-200 hover:bg-slate-100'}`}>
              <strong className="block text-xs leading-5">{template.name}</strong>
              <span className="mt-0.5 block text-[11px] text-slate-500">{count} {count === 1 ? 'text box' : 'text boxes'} · {template.format ? `${template.format.width} × ${template.format.height} px` : 'Size unresolved'}</span>
            </button>;
          })}
        </div>
      </aside>
      {selected ? <section aria-label="Selected layout example" className="min-w-0 space-y-4 rounded-xl border bg-white p-5 lg:sticky lg:top-20 lg:max-h-[calc(100dvh-6rem)] lg:overflow-y-auto">
        {!filtered.some((item) => item._id === selectedId) && <p className="text-xs text-slate-500">This selected template is outside the current filters.</p>}
        <h2 className="text-lg font-semibold">{selected.name}</h2>
        {selected.description && <p className="text-sm text-slate-600">{selected.description}</p>}
        <LayoutTemplatePreview key={selected._id} template={selected} presets={presets} books={books} />
        {!selected.format && <form className="flex flex-wrap items-end gap-2 rounded border border-amber-200 bg-amber-50 p-3" onSubmit={(event) => {
          event.preventDefault();
          if (!window.confirm(`Assign ${width} × ${height} px to this template? Copies will be allowed only on pages with these exact dimensions.`)) return;
          void mutate(() => updateContentLayout(selected._id, { ...selected, format: { width: Number(width), height: Number(height) } }));
        }}>
          <p className="w-full text-sm">Choose this template’s actual page size to enable copying.</p>
          <label className="text-xs">Width (px)<input type="number" required min="1" max="20000" step="1" value={width} onChange={(event) => setWidth(event.target.value)} className="block w-28 rounded border p-2" /></label>
          <label className="text-xs">Height (px)<input type="number" required min="1" max="20000" step="1" value={height} onChange={(event) => setHeight(event.target.value)} className="block w-28 rounded border p-2" /></label>
          <button disabled={saving} className="rounded border bg-white p-2 text-sm">Save page size</button>
        </form>}
        <div className="flex flex-wrap gap-3 border-t pt-4 text-sm">
          <button disabled={saving} className="rounded border px-3 py-2" onClick={() => {
            const name = window.prompt('Template name', selected.name);
            if (name?.trim()) void mutate(() => axios.patch(`/api/content-layouts/${selected._id}`, { name: name.trim() }));
          }}>Rename</button>
          <button disabled={saving} className="rounded border px-3 py-2" onClick={() => {
            const name = window.prompt('Name for the template copy', `${selected.name} (Copy)`);
            if (name?.trim()) void mutate(() => createContentLayout({ ...selected, name: name.trim() }));
          }}>Duplicate</button>
          <button disabled={saving} className="rounded border border-red-200 px-3 py-2 text-red-700" onClick={() => {
            if (window.confirm(`Delete template "${selected.name}"? Pages that already copied it keep their layouts.`)) void mutate(async () => { await deleteContentLayout(selected._id); setSelectedId(''); });
          }}>Delete template</button>
        </div>
      </section> : <p className="p-6 text-sm text-slate-500">Choose a layout to see every text box with example wording.</p>}
    </div>
  </main>;
}
